import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Calendar as CalendarIcon, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', goal: '' });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form, { withCredentials: true });
      setDone(true);
      toast({ title: 'Booked!', description: 'Tap WhatsApp on the next screen for the fastest response.' });
      setTimeout(() => {
        setDone(false);
        setForm({ name: '', email: '', phone: '', goal: '' });
        onClose();
      }, 2800);
    } catch (err) {
      toast({ title: 'Booking failed', description: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-[#faf7f1] border-none rounded-3xl p-0 overflow-hidden">
        <div className="bg-[#3a5a2c] text-white p-8">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl text-white flex items-center gap-3">
              <CalendarIcon className="w-6 h-6" />
              Book your free call
            </DialogTitle>
            <DialogDescription className="text-white/80 pt-2">
              15-minute discovery call. No commitments. Real answers.
            </DialogDescription>
          </DialogHeader>
        </div>

        {done ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#e8f2e2] mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-8 h-8 text-[#3a5a2c]" />
            </div>
            <h3 className="font-serif text-2xl text-[#1a2b17]">Thank you, {form.name || 'friend'}!</h3>
            <p className="text-[#4a5544] mt-2 text-sm">
              Your slot request is saved. For the fastest response, tap WhatsApp below — we&apos;ll reach out within a few hours.
            </p>
            <a
              href={`https://wa.me/919545405936?text=${encodeURIComponent(`Hi, I just booked a free consultation.\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nGoal: ${form.goal}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#20b45a] text-white font-medium transition"
            >
              Continue on WhatsApp
            </a>
            <p className="text-[10px] tracking-widest uppercase text-[#7a8471] mt-4">Email confirmation coming soon</p>
          </div>
        ) : (
          <form onSubmit={submit} className="p-8 space-y-4">
            <div>
              <Label className="text-[#1a2b17] text-sm">Full name</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1.5 bg-white border-neutral-200 h-11 rounded-xl"
                placeholder="Jane Doe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1a2b17] text-sm">Email</Label>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 bg-white border-neutral-200 h-11 rounded-xl"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <Label className="text-[#1a2b17] text-sm">Phone</Label>
                <Input
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5 bg-white border-neutral-200 h-11 rounded-xl"
                  placeholder="+91 95454 05936"
                />
              </div>
            </div>
            <div>
              <Label className="text-[#1a2b17] text-sm">What&apos;s your goal?</Label>
              <Textarea
                value={form.goal}
                onChange={(e) => setForm({ ...form, goal: e.target.value })}
                className="mt-1.5 bg-white border-neutral-200 rounded-xl min-h-[90px]"
                placeholder="Weight loss, PCOS, energy, gut health..."
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#3a5a2c] hover:bg-[#2d4622] text-white mt-2"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Booking…</>) : 'Confirm my slot'}
            </Button>
            <p className="text-xs text-center text-[#7a8471]">
              By booking, you agree to our friendly terms. We&apos;ll never spam.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
